import { ConfigProvider } from 'antd';
import { CssBaseline, GlobalStyles, ThemeProvider, createTheme } from '@mui/material';
import './admin.css';
import { adminFont, adminType, adminTypeVariables } from './typography';

export const adminColors = {
  ocean: '#0B5A78', oceanHover: '#084862', mist: '#E5F2F7', mistBorder: '#C5DFEA',
  background: '#F2F5F8', surface: '#FFFFFF', panel: '#F8FAFC', sunken: '#EDF2F6',
  border: '#DFE7EE', strongBorder: '#CDD8E1', text: '#0D2B40', secondary: '#45596A', muted: '#5B6F80',
  glow: '#2FD9CB', glowSoft: '#E3F9F6', success: '#1D6F45', warning: '#8A5A00', error: '#A1281F',
};
const bodyFont = adminFont;
const headingFont = bodyFont;
const touch = '@media (pointer: coarse)';
const focus = { outline: `2px solid ${adminColors.ocean}`, outlineOffset: 2 };

export const adminTheme = createTheme({
  palette: { primary: { main: adminColors.ocean, dark: adminColors.oceanHover },
    secondary: { main: adminColors.ocean }, error: { main: adminColors.error },
    success: { main: adminColors.success }, warning: { main: adminColors.warning },
    background: { default: adminColors.background, paper: adminColors.surface },
    text: { primary: adminColors.text, secondary: adminColors.secondary }, divider: adminColors.border },
  typography: { fontFamily: bodyFont, fontSize: 14,
    h1: adminType.pageTitle,
    body1: adminType.body, body2: adminType.secondary,
    caption: adminType.caption, subtitle1: adminType.description,
    h2: { fontFamily: headingFont, fontSize: 22, fontWeight: 700 },
    h5: { fontFamily: headingFont, fontSize: 22, fontWeight: 700, lineHeight: 1.1 },
    h6: { fontFamily: headingFont, fontSize: 18, fontWeight: 700 },
    button: { textTransform: 'none', fontSize: 12.5, fontWeight: 600 } },
  shape: { borderRadius: 9 },
  components: {
    MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: {
      root: { borderRadius: 999, minHeight: 26.5, padding: '7px 14px', fontSize: 12.5, lineHeight: 1,
        whiteSpace: 'normal', textTransform: 'none', gap: 8,
        '&.Mui-focusVisible': focus, '&.Mui-disabled': { background: adminColors.sunken, color: adminColors.muted },
        [touch]: { minHeight: 44 }, '& .MuiSvgIcon-root': { fontSize: 12 } },
      outlined: { padding: '6px 14px', borderColor: adminColors.strongBorder },
      startIcon: { margin: 0 }, endIcon: { margin: 0 },
    } },
    MuiIconButton: { styleOverrides: { root: { width: 26.5, height: 26.5, padding: 6, color: adminColors.ocean,
      '&.Mui-focusVisible': focus, '& .MuiSvgIcon-root': { fontSize: 14 }, [touch]: { width: 44, height: 44 } } } },
    MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { rounded: { borderRadius: 14 } } },
    MuiCard: { styleOverrides: { root: { border: `1px solid ${adminColors.border}`, boxShadow: 'none' } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 18, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)', margin: 16 } } },
    MuiDialogTitle: { styleOverrides: { root: { padding: '20px 24px 12px', ...adminType.dialogTitle } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px', gap: 8, flexWrap: 'wrap' } } },
    MuiModal: { defaultProps: { className: 'admin-modal' }, styleOverrides: { root: {
      '& > .MuiBox-root': { maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
        height: 'auto', borderRadius: '18px', padding: 24, background: adminColors.surface, boxShadow: '0 10px 30px rgba(13,43,64,.12)' },
    } } },
    MuiBackdrop: { styleOverrides: { root: { backgroundColor: 'rgba(13,43,64,.36)' } } },
    MuiInputBase: { styleOverrides: { root: { fontSize: 13.5 }, input: { '&::placeholder': { color: adminColors.muted, opacity: 1 } } } },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 9, background: adminColors.surface,
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${adminColors.mist}` } }, notchedOutline: { borderColor: adminColors.strongBorder } } },
    MuiTab: { styleOverrides: { root: { minHeight: 42, fontSize: 13, textTransform: 'none', padding: '10px 16px', [touch]: { minHeight: 44 } } } },
    MuiTabs: { styleOverrides: { root: { minHeight: 42, borderBottom: `1px solid ${adminColors.border}` } } },
    MuiPaginationItem: { styleOverrides: { root: { borderRadius: 999, borderColor: adminColors.border,
      '&.Mui-selected': { backgroundColor: adminColors.mist, borderColor: adminColors.mistBorder, color: adminColors.ocean },
      [touch]: { minWidth: 44, height: 44 } } } },
    MuiChip: { styleOverrides: { root: { borderRadius: 6, fontSize: 12 }, clickable: { borderRadius: 999, [touch]: { minHeight: 44 } } } },
    MuiTooltip: { styleOverrides: { tooltip: { background: adminColors.text, fontSize: 12, borderRadius: 6 } } },
  },
});

const variables = Object.fromEntries(Object.entries(adminColors).map(([key, value]) => [`--admin-${key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`, value]));

export default function AdminTheme({ children }) {
  return <ThemeProvider theme={adminTheme}>
    <ConfigProvider theme={{ token: { colorPrimary: adminColors.ocean, colorText: adminColors.text, colorTextSecondary: adminColors.secondary,
      colorBorder: adminColors.strongBorder, colorBgContainer: adminColors.surface, borderRadius: 9, fontFamily: bodyFont, fontSize: 14, lineHeight: 1.5, lineHeightHeading1: 1.1, fontSizeHeading1: 30 } }}>
      <CssBaseline />
      <GlobalStyles styles={{ ':root': { ...variables, ...adminTypeVariables },
        body: { margin: 0, ...adminType.body, color: adminColors.text, background: adminColors.background } }} />
      {children}
    </ConfigProvider>
  </ThemeProvider>;
}
